import React, { forwardRef, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import { Grid, InputLabel } from "@material-ui/core";
import OutlinedTextField from '../../OutlinedTextField';

import translate from "../../../locales";
import ValueListDialog from "../../Dialogs/ValueListDialog";
import SwitchPositionTableDialog from "../../Dialogs/SwitchPositionTableDialog";
import SwitchEmissionProfileTableDialog from "../../Dialogs/SwitchEmissionProfileTableDialog";
import { EMISSION_PROFILES } from "../../../_helpers/utility";

const __t = translate;

const ParameterForm = forwardRef((props, ref) => {
    const [name, setName] = useState(props.parameter ? props.parameter.name : "");
    const [nameError, setNameError] = useState(null);
    const [positions, setPositions] = useState(props.parameter ? props.parameter.positions : []);
    const [positionsError, setPositionsError] = useState(null);
    const [groups, setGroups] = useState(props.parameter ? props.parameter.groups : []);
    const [emissionProfiles, setEmissionProfiles] = useState(props.parameter ? props.parameter.emissionProfiles : []);

    const [valueModal, setValueModal] = useState(null);
    const [switchPositionModal, setSwitchPositionModal] = useState(false);
    const [epModal, setEpModal] = useState(false);

    useImperativeHandle(ref, () => ({
        getData: () => {
            let hasError = false;

            setNameError(null);
            setPositionsError(null);

            if (!name || name.trim() === '') {
                setNameError(__t('name_required'));
                hasError = true;
            }

            if (!positions || positions.length === 0) {
                setPositionsError(__t('positions_required'));
                hasError = true;
            }

            if (hasError)
                return false;

            return {
                name: name,
                positions: positions,
                groups: groups,
                emissionProfiles: emissionProfiles
            };
        }
    }))

    return (
        <>
            <ValueListDialog
                open={valueModal != null}
                title={valueModal != null ? __t(valueModal) : ''}
                values={valueModal == 'groups' ? groups : emissionProfiles}
                onCancel={() => setValueModal(null)}
                onSubmit={(values) => {
                    if (valueModal == 'groups') {
                        setGroups(values.sort());
                    } else {
                        setEmissionProfiles(values.sort());
                    }

                    setValueModal(null);
                }}
            />
            <SwitchEmissionProfileTableDialog
                open={epModal}
                allEmissionProfiles={EMISSION_PROFILES}
                emissionProfiles={emissionProfiles}
                onCancel={() => setEpModal(false)}
                onSubmit={(values) => {
                    setEmissionProfiles(values);
                    setEpModal(false);
                }}
            />
            <SwitchPositionTableDialog
                open={switchPositionModal}
                allPositions={props.allPositions}
                positions={positions}
                onCancel={() => setSwitchPositionModal(false)}
                onSubmit={(values) => {
                    setPositions(values);
                    setSwitchPositionModal(false);
                }}
            />
            <Grid container spacing={8} direction="row" justify="flex-start" alignItems="center">
                <Grid item xs={2}>
                    <InputLabel shrink required>{__t('name')}:</InputLabel>
                </Grid>
                <Grid item xs={4}>
                    <OutlinedTextField
                        value={name}
                        error={nameError}
                        onChange={(e) => setName(e.target.value.replace(" ", "_"))}
                        inputProps={{ maxLength: 70, style: { textTransform: 'uppercase' } }}
                        disabled={props.readonly}
                    />
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={2}>
                    <InputLabel shrink required>{__t('positions')}:</InputLabel>
                </Grid>
                <Grid item xs={10}>
                    <OutlinedTextField
                        value={positions.map(p => p.name).join(', ')}
                        error={positionsError}
                        inputProps={{ style: { textTransform: 'uppercase' }, readOnly: true }}
                        onClick={() => {
                            setSwitchPositionModal(true);
                        }}
                        disabled={props.readonly}
                    />
                </Grid>
                {props.showEmissionProfiles ?
                    <>
                        <Grid item xs={2}>
                            <InputLabel shrink >{__t('emission_profiles')}:</InputLabel>
                        </Grid>
                        <Grid item xs={10}>
                            <OutlinedTextField
                                value={emissionProfiles.join(', ')}
                                inputProps={{ style: { textTransform: 'uppercase' }, readOnly: true }}
                                onClick={() => {
                                    setEpModal(true);
                                }}
                                disabled={props.readonly}
                            />
                        </Grid>
                    </>
                    :
                    null
                }
                <Grid item xs={2}>
                    <InputLabel shrink>{__t('groups')}:</InputLabel>
                </Grid>
                <Grid item xs={10}>
                    <OutlinedTextField
                        value={groups.join(', ')}
                        inputProps={{ style: { textTransform: 'uppercase' }, readOnly: true }}
                        onClick={() => {
                            setValueModal('groups');
                        }}
                        disabled={props.readonly}
                    />
                </Grid>
            </Grid>
        </>
    )
});

ParameterForm.propTypes = {
    allPositions: PropTypes.array.isRequired,
    parameter: PropTypes.object,
    readonly: PropTypes.bool,
    showEmissionProfiles: PropTypes.bool
}

export default ParameterForm;